import { Button, Typography as FoodsTypography } from "@foodpilot/foods";
import {
    Box,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography as MuiTypography,
    useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { FoodsBottomSheetDialog } from "src/components/Dialog/FoodsBottomSheetDialog";
import { FoodsLoadingIndicator } from "src/components/EntityForm/FoodsFields";
import { RemoteErrorAlert } from "src/components/PageState/RemoteErrorAlert";

type TransportDeleteConfirmDialogProps = {
    appearance?: "legacy" | "product-v2";
    open: boolean;
    title?: string;
    error?: string | null;
    isDeleting?: boolean;
    onClose: () => void;
    onConfirm: () => void;
};

export const TransportDeleteConfirmDialog = ({
    appearance = "product-v2",
    open,
    title,
    error,
    isDeleting = false,
    onClose,
    onConfirm,
}: TransportDeleteConfirmDialogProps) => {
    const { t } = useTranslation();
    const theme = useTheme();

    if (appearance === "legacy") {
        return (
            <Dialog
                open={open}
                onClose={isDeleting ? undefined : onClose}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    "data-appearance": "legacy",
                    sx: {
                        borderRadius: theme.spacing(1.5),
                        backgroundColor: theme.custom.grey[100],
                    },
                }}
            >
                <DialogTitle component="div">
                    <MuiTypography component="h2" variant="h3" sx={{ fontSize: theme.spacing(2.25) }}>
                        {t("transport.delete_dialog.title", "Delete transport?")}
                    </MuiTypography>
                </DialogTitle>
                <DialogContent>
                    {error && <RemoteErrorAlert message={error} status="server-error" />}
                    {title && (
                        <MuiTypography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                            {title}
                        </MuiTypography>
                    )}
                    <MuiTypography variant="body2" sx={{ color: theme.custom.grey[2000] }}>
                        {t(
                            "transport.delete_dialog.message",
                            "This action is permanent. Are you sure you want to delete this transport?",
                        )}
                    </MuiTypography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button variant="ternary" onClick={onClose} disabled={isDeleting}>
                        {t("glossary.cancel", "Cancel")}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={onConfirm}
                        disabled={isDeleting}
                        sx={{
                            background: `linear-gradient(${theme.palette.error[900]}, ${theme.palette.error[700]})`,
                        }}
                    >
                        {isDeleting ?
                            <CircularProgress size={theme.spacing(2.5)} />
                        :   t("common.action.delete", "Delete")}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <FoodsBottomSheetDialog
            open={open}
            setOpen={() => undefined}
            onClose={isDeleting ? undefined : onClose}
            aria-labelledby="transport-delete-dialog-title"
            aria-describedby="transport-delete-dialog-description"
            disableEscapeKeyDown={isDeleting}
            title={
                <FoodsTypography id="transport-delete-dialog-title" component="h2" variant="big-semibold">
                    {t("transport.delete_dialog.title", "Delete transport?")}
                </FoodsTypography>
            }
            content={
                <Stack gap={theme.spacing(3)} sx={{ p: theme.spacing(3) }}>
                    {error && <RemoteErrorAlert message={error} status="server-error" />}
                    <Box>
                        {title && (
                            <FoodsTypography variant="body-semibold" sx={{ display: "block", mb: 1 }}>
                                {title}
                            </FoodsTypography>
                        )}
                        <FoodsTypography
                            id="transport-delete-dialog-description"
                            variant="body2"
                            sx={{ color: theme.custom.grey[2000] }}
                        >
                            {t(
                                "transport.delete_dialog.message",
                                "This action is permanent. Are you sure you want to delete this transport?",
                            )}
                        </FoodsTypography>
                    </Box>
                    <Stack direction="row" justifyContent="flex-end" gap={theme.spacing(1)}>
                        <Button variant="ternary" onClick={onClose} disabled={isDeleting}>
                            {t("glossary.cancel", "Cancel")}
                        </Button>
                        <Button variant="delete" onClick={onConfirm} disabled={isDeleting}>
                            {isDeleting ?
                                <FoodsLoadingIndicator label={t("common.loading", "Loading...")} />
                            :   t("common.action.delete", "Delete")}
                        </Button>
                    </Stack>
                </Stack>
            }
        />
    );
};

export default TransportDeleteConfirmDialog;
